
import {
    ApplicationRef,
    ChangeDetectionStrategy,
    Component,
    ComponentRef,
    ElementRef,
    EnvironmentInjector,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    createComponent,
    inject
} from "@angular/core";
import { DayPilot, DayPilotModule, DayPilotSchedulerComponent } from "daypilot-pro-angular";
import {
    debounce,
    differenceWith,
    groupBy,
    orderBy,
    values
} from "lodash";
import moment from "moment";
import * as range from "moment-range";
import { Subject, firstValueFrom } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Produktionsstatus, VirkesorderStatus } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CalendarService } from "~app/common/services/calendar.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager } from "~app/common/services/cqs/commandManager";
import { EventService } from "~app/common/services/cqs/event.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { SetMaskinlagCommand } from "~app/features/planera/commands/SetMaskinlagCommand";
import { ArbetsorderNamnUpdatedMessage } from "~app/features/planera/events/ArbetsorderNamnUpdatedMessage";
import { ArbetsorderOperationDeletedEvent } from "~app/features/planera/events/ArbetsorderOperationDeletedEvent";
import { ArbetsorderOperationUpdatedEvent } from "~app/features/planera/events/ArbetsorderOperationUpdatedEvent";
import { MinaMaskinlagUpdatedEventMessage } from "~app/features/planera/events/MinaMaskinlagUpdatedEventMessage";
import { IMaskinLagWithOperations, IMaskinlagForArbetsorderResponse } from "~app/features/planera/mina-maskinlag/mina-maskinlag.models";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IOperation } from "~app/features/planera/model/IOperation";
import { ChangeMaskinlagForArbetsorderOperationCommand } from "../commands/ChangeMaskinlagForArbetsorderOperationCommand";
import { ResizeArbetsorderOperationCommand } from "../commands/ResizeArbetsorderOperationCommand";
import { PlaneraColorExplanationPopoverComponent } from "../planera-color-explanation-popover/planera-color-explanation-popover.component";
import { ArbetsorderKartSymbolService } from "../services/arbetsorder-kart-symbol.service";
import { ArbetsorderMediatorService } from "../services/arbetsorder-mediator.service";
import { Operation } from "./Operation";
import { OperationShifter } from "./OperationShifter";
import { ArbetsorderPlaneringSchedulerTexts } from "./arbetsorder-planering-scheduler.texts";
import { ArbetsorderSchedulerPopoverNgxComponent } from "./arbetsorder-scheduler-popover-ngx/arbetsorder-scheduler-popover-ngx/arbetsorder-scheduler-popover-ngx.component";
import { DateErrorType } from "./models/DateErrorType";
import { IMaskinLagSchedulerEvent } from "./models/IMaskinLagSchedulerEvent";
import { SchemaPopupInfoDTO } from "./models/SchemaPopupInfoDTO";
import { ArbetsorderPlaneringSchedulerService } from "./services/arbetsorder-planering-scheduler.service";
import { ArbetsorderPlaneringSchedulerSettingsComponent } from "./settings/arbetsorder-planering-scheduler-settings.component";

export const momentRange = range.extendMoment(moment as any);

@Component({
    selector: "rask-arbetsorder-planering-scheduler",
    styleUrls: ["./arbetsorder-planering-scheduler.component.scss"],
    templateUrl: "./arbetsorder-planering-scheduler.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ArbetsorderPlaneringSchedulerSettingsComponent,
        PlaneraColorExplanationPopoverComponent,
        DayPilotModule
    ]
})
export class ArbetsorderPlaneringSchedulerComponent implements OnInit, OnDestroy {
    readonly maxZoom = 200;
    readonly minZoom = 10;

    arbetsorder: IArbetsorderDetail;
    daypilotInitialized: boolean;
    @ViewChild("scheduler") scheduler: DayPilotSchedulerComponent;
    schedulerConfig: DayPilot.SchedulerConfig;
    schedulerEvents: IMaskinLagSchedulerEvent[];
    startDate: string; // short date
    texts = ArbetsorderPlaneringSchedulerTexts.texts;

    private readonly antalDagarAttVisa = 365;
    private readonly appRef = inject(ApplicationRef);
    private readonly arbetsorderKartSymbolService = inject(ArbetsorderKartSymbolService);
    private readonly arbetsorderPlaneringSchedulerService = inject(ArbetsorderPlaneringSchedulerService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly calendarService = inject(CalendarService);
    private readonly commandService = inject(CommandService);
    private readonly currentArbetsorderId: number;
    private readonly dayResolutionThreshold = 20;
    private readonly defaultZoom = 20;
    private readonly elementRef = inject(ElementRef);
    private readonly envInjector = inject(EnvironmentInjector);
    private readonly eventClickDebounce = debounce((arg: DayPilot.SchedulerEventClickArgs) => {
        this.eventClick(arg);
    }, 50);
    private readonly eventService = inject(EventService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly ngZone = inject(NgZone);
    private readonly planeraArbetsorderMediatorService = inject(ArbetsorderMediatorService);

    private activePopover?: ComponentRef<ArbetsorderSchedulerPopoverNgxComponent>;
    private dayResolution = false;
    private lastFetchedMaskinlagDate: string;
    private scrollDebounce: any;
    private scrollTarget: any;
    private selectedMinaMaskinlag: IMaskinLagWithOperations[];
    private timeHeaders: DayPilot.TimeHeaderData[];
    private zoomDebounce: any;

    // eslint-disable-next-line sonarjs/public-static-readonly
    static cssClassActiveEvent = "event-active";

    get zoom(): number | undefined {
        return (this.schedulerConfig) ? this.schedulerConfig.cellWidth : this.defaultZoom;
    }

    async arbetsorderChanged(currentArbetsorder: IArbetsorderDetail, previousArbetsorderId?: number) {
        this.lastFetchedMaskinlagDate = undefined;

        let startDate: string;
        if (!previousArbetsorderId && !currentArbetsorder) {
            // From list fresh
            startDate = this.defaultStartDate();
        } else if (!previousArbetsorderId && currentArbetsorder) {
            // From list to arbetsorder
            startDate = undefined;
        } else if (previousArbetsorderId && !currentArbetsorder) {
            // From arbetsorder to list
            startDate = this.defaultStartDate();
        } else if (previousArbetsorderId !== currentArbetsorder.id && this.isArbetsorderInView(currentArbetsorder.id)) {
            // Arbetsorder in view
            startDate = this.startDate;
        } else if (previousArbetsorderId !== currentArbetsorder.id) {
            // Changed arbetsorder
            startDate = undefined;
        }

        await this.loadMaskinlag(startDate);
        if (this.arbetsorder && !startDate) {
            startDate = this.getStartDateFromOperationOnArbetsorder();
        }

        if (!startDate) {
            // Startdate is still not set -> set default
            startDate = this.defaultStartDate();
        }

        await this.loadHelgdagar(moment(startDate).subtract(this.antalDagarAttVisa, "d").toISOString(),
            moment(startDate).add(this.antalDagarAttVisa, "d").toISOString());

        if (!this.schedulerEvents) {
            await this.bindData(startDate);
            return;
        }

        this.syncArbetsorderRowVersion();
        if (previousArbetsorderId !== currentArbetsorder?.id) {
            await this.bindData(startDate);
        } else {
            this.syncPreviousArbetsorder(previousArbetsorderId);
            this.focusOnArbetsorder();
        }

        // The arbetsorder is updated (e.g. with new maskinlag) -> Make sure to scroll the date into view
        this.scrollToDate(this.getSchedulerStartDate());
    }

    async bindData(startDate: string): Promise<void> {
        this.startDate = startDate;
        let zoom = await this.arbetsorderPlaneringSchedulerService.getZoom();
        if (!zoom) {
            zoom = this.defaultZoom;
        }

        this.dayResolution = zoom >= this.dayResolutionThreshold;
        this.timeHeaders = this.getTimeHeadersForZoomLevel(zoom);

        this.schedulerConfig = this.getSchedulerConfig(zoom);

        const canEdit = this.authenticationService.canEdit();
        const cfg = this.getSchedulerEvents(canEdit);
        this.schedulerConfig.resources = cfg.resources;
        this.schedulerEvents = cfg.events; // Events (Korvarna)
        this.focusOnArbetsorder();

        if (!this.scrollTarget) {
            this.scrollTarget = this.elementRef.nativeElement.querySelector(".scheduler_default_scrollable");
            this.scrollTarget.addEventListener("wheel", this.onZoomChanged);
        }
    }

    hasDateErrorHelper = (
        operationStartDate: DayPilot.Date,
        operationEndDate: DayPilot.Date,
        minDate: moment.Moment,
        maxDate: moment.Moment) => moment(operationStartDate.toDateLocal()).isBefore(minDate) || moment(operationEndDate.toDateLocal()).isAfter(maxDate);

    getDateError = (operationStartDate: DayPilot.Date, operationEndDate: DayPilot.Date, o: IMaskinLagSchedulerEvent) => {
        let dateError = DateErrorType.None;
        if (this.hasDateErrorHelper(operationStartDate,
            operationEndDate,
            moment(o.avverkningFarStartaDatum),
            moment(o.avverkningGiltigtTillDatum))) {
            // eslint-disable-next-line no-bitwise
            dateError |= DateErrorType.Avverkningsanmalan;
        }
        if (o.certifikatStartdatum && o.certifikatSlutdatum &&
            this.hasDateErrorHelper(operationStartDate,
                operationEndDate,
                moment(o.certifikatStartdatum),
                moment(o.certifikatSlutdatum))) {
            // eslint-disable-next-line no-bitwise
            dateError |= DateErrorType.Certifikat;
        }
        if (o.kontraktGiltigtFrom && o.kontraktGiltigtTom &&
            this.hasDateErrorHelper(operationStartDate,
                operationEndDate,
                moment(o.kontraktGiltigtFrom),
                moment(o.kontraktGiltigtTom))) {
            // eslint-disable-next-line no-bitwise
            dateError |= DateErrorType.Kontrakt;
        }

        return dateError;
    };

    getSchedulerStartDate = (): Date => moment(this.startDate).startOf("day").toDate();

    handleOperationDeletedEvent = (event: ArbetsorderOperationUpdatedEvent) => {
        const matchedEvent = this.schedulerEvents.find(p => p.arbetsorderId === event.arbetsorderId && p.operationtyp === event.typ);
        if (matchedEvent) {
            const dpEvent = this.scheduler.control.events.find(matchedEvent.id.toString());
            if (!dpEvent) {
                return;
            }

            this.scheduler.control.events.remove(dpEvent);
        }
    };

    handleUpdatedNamn = (event: ArbetsorderNamnUpdatedMessage) => {
        const matchedEvent = this.schedulerEvents.find(p => p.arbetsorderId === event.id);
        if (matchedEvent) {
            matchedEvent.text = event.namn;
            this.scheduler.control.events.update(matchedEvent);
        }
    };

    /**
     * Gör så att daypilot scheduler (schema-vyn i planera) uppdaterar till en hanterbar storlek
     */
    handleWindowScroll = (event: any): void => {
        if (this.scrollDebounce) {
            this.scrollDebounce.cancel();
        }

        this.scrollDebounce = debounce(() => {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const parentBlock = document.querySelector("#block-daypilot") as HTMLElement;
            const viewPortHeight = document.documentElement.clientHeight;
            const footerHeight = 60;
            const footerWithPaddings = 220;
            const usableViewPortHeight = (viewPortHeight - footerWithPaddings);
            const parentBlockTop = parentBlock.getBoundingClientRect().top;
            const currentHeight = parentBlock.clientHeight;
            const maxHeight = usableViewPortHeight;
            const newHeight = (viewPortHeight - Math.max(parentBlockTop, 0)) - footerHeight;

            if (newHeight < maxHeight) {
                parentBlock.style.height = `${newHeight}px`;
                this.scheduler?.control?.show();
            } else if (currentHeight < maxHeight) {
                parentBlock.style.height = `${maxHeight}px`;
                this.scheduler?.control?.show();
            }
            this.scrollDebounce = undefined;
            this.closePopover();
        }, 20);

        this.scrollDebounce();
    };

    maskinlagChangedInSettings = async () => {
        // Mellanlagrar datumet då DayPilot skrollar till dagens datum när korvarna (DayPilot.events) och indirekt raderna (DayPilot.resources) uppdateras
        // Detta gör så att schemat behåller sin nuvarande vy istället för att hoppa till annat datum än vad man hade vid förändringen av maskinlag
        const viewPortStartDate = DateUtils.shortDateString(moment((this.scheduler.control.getViewPort().start as any).value).startOf("d"));

        await this.loadMaskinlag(this.startDate);

        const canEdit = this.authenticationService.canEdit();
        const cfg = this.getSchedulerEvents(canEdit);
        this.schedulerConfig.resources = cfg.resources;
        this.schedulerEvents = cfg.events;
        this.scrollToDate(moment(viewPortStartDate).toDate());
    };

    moveOperationToMaskinlag = async (maskinlagId: number, arbetsorderId: number, startdatum: Date, args) => {
        const commandManager = new CommandManager();
        commandManager.updateCommand(new ChangeMaskinlagForArbetsorderOperationCommand((instance) => {
            instance.maskinlagId = maskinlagId;
            instance.arbetsorderId = arbetsorderId;
            instance.startdatum = startdatum;
        }));

        await this.invokeWithArgs(args, commandManager);
    };

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();

        if (this.scrollTarget) {
            this.scrollTarget.removeEventListener("wheel", this.onZoomChanged);
        }

        window.removeEventListener("scroll", this.handleWindowScroll, true);
        window.removeEventListener("resize", this.handleWindowScroll, true);
    }

    async ngOnInit() {
        this.eventService.subscribeEvent<ArbetsorderOperationUpdatedEvent>(ArbetsorderOperationUpdatedEvent.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(this.handleOperationUpdatedEvent);

        this.eventService.subscribeEvent<ArbetsorderNamnUpdatedMessage>(ArbetsorderNamnUpdatedMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(this.handleUpdatedNamn);

        this.eventService.subscribeEvent<ArbetsorderOperationDeletedEvent>(ArbetsorderOperationDeletedEvent.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(this.handleOperationDeletedEvent);

        this.eventService.subscribeEvent<MinaMaskinlagUpdatedEventMessage>(MinaMaskinlagUpdatedEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(this.maskinlagChangedInSettings);

        this.planeraArbetsorderMediatorService.arbetsorder
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(changeEvent => {
                this.arbetsorder = changeEvent?.arbetsorderWrapper?.arbetsorder;
                this.arbetsorderChanged(this.arbetsorder, changeEvent?.previousArbetsorder?.id);
            });

        window.addEventListener("scroll", this.handleWindowScroll, true);
        window.addEventListener("resize", this.handleWindowScroll, true);
    }

    onSchedulerEventChanging = (args: DayPilot.SchedulerEventMovingArgs | DayPilot.SchedulerEventResizingArgs) => {
        // args.start & args.end är det "nya" datumet som ska appliceras medan args.e.data är "gamla"

        const dateError = this.getDateError(args.start, args.end, args.e.data);
        if (dateError !== DateErrorType.None) {
            args.right.html = this.arbetsorderPlaneringSchedulerService.buildHtmlForMoveError(dateError);
            args.right.enabled = true;
            args.left.enabled = false;
        }

        if ((args as DayPilot.SchedulerEventMovingArgs).external) {
            args.right.html = args.e.data.namn;
        }
    };

    resizeOperation = async (maskinlagId: number, arbetsorderId: number, startdatum: Date, slutdatum: Date, oldEvent: IMaskinLagSchedulerEvent, args) => {
        const commandManager = new CommandManager();
        commandManager.updateCommand(new ResizeArbetsorderOperationCommand((instance) => {
            instance.maskinlagId = maskinlagId;
            instance.arbetsorderId = arbetsorderId;
            instance.startdatum = startdatum;
            instance.slutdatum = slutdatum;
        }));

        await this.invokeWithArgs(args, commandManager);
    };

    setCssClassForEvent = (o: IOperation) => {
        const isActiveOperation = this.arbetsorder != null && this.arbetsorder.id === o.arbetsorderId;
        return this.arbetsorderPlaneringSchedulerService
            .getClassNamesForOperation(o, isActiveOperation);
    };

    setDateError = (o: IMaskinLagSchedulerEvent) => {
        o.tags.dateError = this.getDateError(o.start as DayPilot.Date, o.end as DayPilot.Date, o) !== DateErrorType.None;
    };

    startDateChange($event: string): void {
        this.scrollToDate(moment($event).toDate());
    }

    zoomChange($event: number): void {
        this.setZoom($event);
    }

    protected focusOnArbetsorder() {
        setTimeout(() => {
            const operationOfCurrentArbetsorder = this.getOperationFromArbetsorder();
            if (!operationOfCurrentArbetsorder) {
                return;
            }
            const viewEventmatch = this.isArbetsorderInView(operationOfCurrentArbetsorder.arbetsorderId);

            // Skrolla i X-led (datum) om operationen inte syns
            if (operationOfCurrentArbetsorder && !this.isOperationInDateView(operationOfCurrentArbetsorder)) {
                this.startDate = this.getStartDateFromOperationOnArbetsorder();
                this.scrollToDate(moment(this.startDate).toDate());
            }

            // Skrolla i Y-led (maskinlag)
            if (operationOfCurrentArbetsorder && !viewEventmatch) {
                const maskinlagOfCurrentArbetsorder = this.getMaskinlagForOperation(operationOfCurrentArbetsorder);
                this.scheduler.control.scrollToResource(`${maskinlagOfCurrentArbetsorder.id}`);
            }
        }, 10);
    }

    private closePopover() {
        const popover = this.activePopover;

        if (popover) {
            this.appRef.detachView(popover.hostView);
            popover.destroy();
        }

        delete this.activePopover;
    }

    private readonly defaultStartDate = (): string => DateUtils.shortDateString(moment().subtract(5, "days").startOf("day").toDate());

    private eventClick(arg: DayPilot.SchedulerEventClickArgs) {
        if (arg.ctrl) {
            if (this.scheduler.control.multiselect.isSelected(arg.e)) {
                this.scheduler.control.multiselect.remove(arg.e);
            } else {
                this.scheduler.control.multiselect.add(arg.e);
            }
            arg.preventDefault();
            return;
        }
        this.closePopover();

        this.arbetsorderPlaneringSchedulerService.getSchemaPopupInfo(arg.e.data.arbetsorderId).subscribe(popupinfo => {
            this.ngZone.run(() => this.openPopover(arg, popupinfo));
        });
    }

    private getMaskinlagForOperation(operation: IOperation): IMaskinLagWithOperations {
        return this.selectedMinaMaskinlag.find(x => x.operationer.find(op => op.id === operation.id));
    }

    private readonly getMaskinlagSchedulerEvent = (
        o: IOperation,
        maskinlagId: number,
        canEdit: boolean): IMaskinLagSchedulerEvent => {
        const startOp = new DayPilot.Date(moment(o.startdatum).toDate(), true);
        const endOp = new DayPilot.Date(moment(o.slutdatum).toDate(), true);
        const anskaffningsform = (o.arbetsorderAnskaffningsform) ? o.arbetsorderAnskaffningsform : "";
        const affarsform = (o.arbetsorderAffarsform) ? o.arbetsorderAffarsform : "";
        const toolTip = `${affarsform} ${o.namn} ${o.arbetsorderBeteckning}`;

        return {
            start: startOp,
            end: endOp,
            id: o.arbetsorderId,
            operationId: o.id,
            operationtyp: o.operationstyp,
            arbetsorderId: o.arbetsorderId,
            arbetsorderAffarsform: affarsform,
            arbetsorderBeteckning: o.arbetsorderBeteckning,
            arbetsorderAnskaffningsform: anskaffningsform,
            arbetsorderviol3: o.arbetsorderViol3,
            hasTransmissionErrorProduktionViol3: o.hasTransmissionErrorProduktionViol3,
            hasTransmissionErrorTransportViol3: o.hasTransmissionErrorTransportViol3,
            dirtyProduktionsdel: o.dirtyProduktionsdel,
            dirtyTransportdel: o.dirtyTransportdel,
            skickadTillViol3: o.skickadTillViol3,
            resource: maskinlagId.toString(),
            text: o.namn,
            toolTip: toolTip,
            cssClass: this.setCssClassForEvent(o),
            moveDisabled: !canEdit,
            changeMaskinlagDisabled: (!o.raskVo && o.virkesorderStatus && o.virkesorderStatus !== VirkesorderStatus.Makulerad) ||
                !canEdit ||
                o.arbetsorderProduktionsstatus !== Produktionsstatus.ProduktionEjPaborjad,
            avverkningFarStartaDatum: o.avverkningFarStartaDatum,
            avverkningGiltigtTillDatum: o.avverkningGiltigtTillDatum,
            certifikatStartdatum: o.certifikatStartdatum,
            certifikatSlutdatum: o.certifikatSlutdatum,
            kontraktGiltigtFrom: o.kontraktGiltigtFrom,
            kontraktGiltigtTom: o.kontraktGiltigtTom,
            virkesorderNr: o.virkesorderNr,
            virkesorderStatus: o.virkesorderStatus,
            volymArbetsorder: o.volymArbetsorder,
            arbetsorderRowVersion: o.arbetsorderRowVersion,
            clickDisabled: this.currentArbetsorderId === o.arbetsorderId,
            resizeDisabled: !canEdit,
            valsSynkroniseringsstatus: o.valsSynkroniseringsstatus,
            tags: {
                locked: false,
                namn: o.namn,
                dateError: false
            },
            backColor: this.arbetsorderKartSymbolService.getSchemaInfoForArbetsorder(o)
        };
    };

    private getOperationFromArbetsorder(): IOperation {
        if (!this.arbetsorder || !this.selectedMinaMaskinlag) {
            return undefined;
        }

        const getOperationFromMaskinlagForArbetsorder = () => this.selectedMinaMaskinlag
            .map(p => p.operationer)
            .reduce((prev, curr) => prev.concat(curr), [])
            .find(p => p.arbetsorderId === this.arbetsorder.id);

        return getOperationFromMaskinlagForArbetsorder();
    }

    private getPopOverDiv(element: any): HTMLDivElement {
        const popOverClassName = "popover-wrapper";

        if (element.childNodes.length > 1) {
            if (element.childNodes[element.childNodes.length - 1].className !== popOverClassName) {
                element = element.childNodes[element.childNodes.length - 1];
            }
        }

        const popoverWrapper = document.createElement("div");
        popoverWrapper.className = popOverClassName;
        element.appendChild(popoverWrapper);

        return popoverWrapper;
    }

    private getSchedulerConfig(cellWidth: number): DayPilot.SchedulerConfig {
        return {
            treeEnabled: false,
            locale: "sv-se",
            scale: "Day",
            cellDuration: 1440,
            cellWidth: cellWidth || 20,
            infiniteScrollingEnabled: true,
            rowHeaderWidthAutoFit: false,
            rowHeaderWidth: 167,
            dynamicLoading: true,
            linkArrowSize: 12,
            linkHitAreaMinWidth: 12,
            showNonBusiness: true,
            startDate: new DayPilot.Date(this.getSchedulerStartDate(), true),
            separators: [
                {
                    color: "#ff5722",
                    width: 2,
                    location: new DayPilot.Date(),
                    layer: "AboveEvents"
                }
            ],
            days: this.antalDagarAttVisa,
            timeHeaders: this.timeHeaders,
            eventClickHandling: "Enabled",
            resources: [],
            snapToGrid: true,
            rectangleSelectMode: "Row",
            rectangleSelectHandling: "Enabled",
            blockOnCallBack: true,
            allowMultiMove: true,
            allowMultiSelect: true,
            useEventBoxes: "Never",
            heightSpec: "Parent100Pct",
            eventMovingStartEndEnabled: true,
            eventResizingStartEndEnabled: true,
            rowMinHeight: 60,
            eventHeight: 40,
            durationBarVisible: false,
            eventMarginBottom: 4,
            eventMarginLeft: 4,
            rowMarginTop: 8,
            onTimeRangeSelect: (args: DayPilot.SchedulerTimeRangeSelectArgs) => {
                this.scheduler.control.multiselect.clear();
            },
            onScroll: async (args: DayPilot.SchedulerScrollArgs) => {
                args.async = true;
                const viewPortStartDate = DateUtils
                    .shortDateString(moment((args.viewport.start as any).value).startOf("d"));

                if (this.lastFetchedMaskinlagDate && moment(viewPortStartDate).isSameOrAfter(this.lastFetchedMaskinlagDate)) {
                    // Redan laddat allt data
                    setTimeout(() => {
                        args.clearEvents = false;
                        args.events = this.schedulerEvents;
                        args.loaded();
                        this.startDate = viewPortStartDate;
                    });
                } else {
                    args.clearEvents = true;
                    this.startDate = viewPortStartDate;
                    setTimeout(async () => {
                        await this.loadMaskinlag(this.startDate);
                        const canEdit = this.authenticationService.canEdit();

                        const events = this.getSchedulerEvents(canEdit).events;
                        args.events = differenceWith(events, this.schedulerEvents, (a, b) => a.operationId === b.operationId);
                        this.schedulerEvents = events;
                        args.loaded();
                    });
                }

                this.closePopover();

                const viewPortEndDate = DateUtils.shortDateString(moment((args.viewport.end as any).value).startOf("d"));
                if (!this.calendarService.hasLoadedRange(viewPortStartDate, viewPortEndDate)) {
                    this.loadHelgdagar(moment(viewPortStartDate).subtract(this.antalDagarAttVisa, "d").toISOString(),
                        moment(viewPortEndDate).add(this.antalDagarAttVisa, "d").toISOString());
                }
            },
            onBeforeEventRender: (e: DayPilot.SchedulerBeforeEventRenderArgs) => {
                const event = (e.data as IMaskinLagSchedulerEvent);
                e.data.html = this.arbetsorderPlaneringSchedulerService.buildHtmlForEvent(event);
            },
            onEventMove: async (args: DayPilot.SchedulerEventMoveArgs) => {
                args.async = true;
                const data = args.e.data as IMaskinLagSchedulerEvent;

                if (args.external) {
                    await this.handleArbetsorderDrop(args);
                } else if (args.newResource === data.resource) {
                    await this.moveOperationer(args);
                } else if (args.newResource !== data.resource) {
                    const maskinlag = this.selectedMinaMaskinlag
                        .find(p => p.id === parseInt((args.newResource as string), 10));
                    await this.moveOperationToMaskinlag(maskinlag.id, data.arbetsorderId, args.newStart.toDate(), args);
                }
            },
            onEventMoving: (args: DayPilot.SchedulerEventMovingArgs) => {
                this.closePopover();
                const multimove = (args as any).multimove;

                if (multimove && multimove.length > 1) {
                    const anyDisabled = multimove.some(op => op.event.data.moveDisabled);
                    if (anyDisabled) {
                        args.allowed = false;
                        return;
                    }
                }

                if (multimove) {
                    // Får multiselecta och byta maskinlag samtidigt
                    const anyMoved = multimove.some(op => op.event.data.resource !== op.resource && op.event.data.changeMaskinlagDisabled);
                    if (anyMoved) {
                        args.allowed = false;
                        return;
                    }
                }

                this.onSchedulerEventChanging(args);
            },
            onEventClick: (arg) => {
                this.eventClickDebounce(arg);
            },
            onBeforeTimeHeaderRender: (args) => {
                if (args.header.level === 1) {
                    args.header.html = `v${args.header.start.weekNumberISO()}`;
                } else if (args.header.level === 2 && this.dayResolution) {
                    const dayOfWeek = args.header.start.getDayOfWeek();
                    if (dayOfWeek === 0 || dayOfWeek === 6 || this.calendarService.isHelgdag(args.header.start.toDate())) {
                        args.header.backColor = "#d0d0d0";
                        args.header.cssClass = "weekend_header";
                    }
                }
            },
            onBeforeCellRender: (args) => {
                if (!this.dayResolution) {
                    return;
                }

                const jsDate = args.cell.start.toDate();
                if (this.arbetsorderPlaneringSchedulerService.isWeekendOrHelgdag(jsDate)) {
                    args.cell.properties.backColor = "#f9f9f9";
                }
            },
            onEventResize: async (args) => {
                args.async = true;
                const maskinlag = this.selectedMinaMaskinlag
                    .find(p => p.id === parseInt(args.e.data.resource, 10));
                await this.resizeOperation(maskinlag.id, args.e.data.arbetsorderId, args.newStart.toDate(), args.newEnd.toDate(), args.e.data, args);
            },
            onEventResizing: (args) => {
                this.closePopover();
                this.onSchedulerEventChanging(args);
            },
            onAfterRender: (args) => {
                if (!args.isCallBack) {
                    setTimeout(() => {
                        this.daypilotInitialized = true;
                    }, 200);
                }
            }
        };
    }

    private getSchedulerEvents(canEdit: boolean): { resources: DayPilot.ResourceData[]; events: IMaskinLagSchedulerEvent[] } {
        const schedulerEvents: IMaskinLagSchedulerEvent[] = [];
        const resources = [];
        this.selectedMinaMaskinlag.forEach((m) => {
            const maskinLag: DayPilot.ResourceData = {
                id: `${m.id}`,
                name: `${m.csrNummer} - ${m.namn}`,
                html: `${m.csrNummer} -<br />${m.namn}`
            };
            resources.push(maskinLag);

            this.arbetsorderPlaneringSchedulerService.filterOperationerOnTyp(m.operationer).forEach((o) => {
                const operation = this.getMaskinlagSchedulerEvent(o, m.id, canEdit);

                this.setDateError(operation);
                schedulerEvents.push(operation);
            });
        });

        return { resources: resources,
            events: schedulerEvents };
    }

    private getStartDateFromOperationOnArbetsorder(): string {
        const operation = this.getOperationFromArbetsorder();

        if (!operation) {
            return undefined;
        }

        return DateUtils.shortDateString(moment(operation.startdatum).subtract(5, "days").startOf("day").toDate());
    }

    private readonly getTimeHeadersForZoomLevel = (zoom): DayPilot.TimeHeaderData[] => {
        if (this.dayResolution) {
            return [
                { groupBy: "Month" },
                { groupBy: "Week" },
                { groupBy: "Day",
                    format: "d" }
            ];
        }

        return [
            { groupBy: "Month" },
            { groupBy: "Week" }
        ];
    };

    private readonly invokeWithArgs = async (args, commandManager: CommandManager, rollback?: () => void) => {
        try {
            await this.commandService.pushCommands(commandManager);
            args.preventDefault();
            args.loaded();
        } catch {
            rollback();
            args.preventDefault();
            args.loaded();
        }
    };

    private readonly handleArbetsorderDrop = async (args) => {
        const maskinlag = this.selectedMinaMaskinlag.find(p => p.id === parseInt(args.newResource, 10));
        const data = args.e.data;

        const command = new SetMaskinlagCommand(instance => {
            instance.arbetsorderId = args.e.data.id as number;
            instance.maskinlagId = maskinlag.id;
            instance.startdatum = args.newStart;
        });

        const canEdit = this.authenticationService.canEdit();
        const operationToAdd = this.getMaskinlagSchedulerEvent({
            arbetsorderId: data.id,
            arbetsorderBeteckning: data.beteckning,
            arbetsorderAffarsform: data.affarsform,
            arbetsorderAnskaffningsform: data.anskaffningsform,
            arbetsorderViol3: data.arbetsorderViol3,
            arbetsorderRowVersion: data.arbetsorderRowVersion,
            arbetsorderProduktionsstatus: data.produktionsstatus,
            namn: data.namn,
            operationstyp: data.operationstyp,
            avverkningFarStartaDatum: data.avverkningFarStartaDatum,
            avverkningGiltigtTillDatum: data.avverkningGiltigtTillDatum,
            certifikatStartdatum: data.certifikatStartdatum,
            certifikatSlutdatum: data.certifikatSlutdatum,
            startdatum: args.start,
            slutdatum: args.end,
            volymArbetsorder: data.volymArbetsorder,
            raskVo: data.raskVo
        } as IOperation, maskinlag.id, canEdit);
        this.scheduler.control.events.add(operationToAdd);

        const commandManager = new CommandManager();
        commandManager.updateCommand(command);
        await this.invokeWithArgs(args, commandManager, () => {
            this.scheduler.control.events.remove(operationToAdd.id);
        });

    };

    private readonly handleOperationUpdatedEvent = (event: ArbetsorderOperationUpdatedEvent) => {
        const matchedEvent = this.schedulerEvents.find(p => p.arbetsorderId === event.arbetsorderId && p.operationtyp === event.typ);
        if (matchedEvent) {
            const resource = this.schedulerConfig.resources.find(p => p.id.toString() === event.maskinlagId.toString());
            if (!resource) {
                const dpEvent = this.scheduler.control.events.find(matchedEvent.id.toString());
                this.scheduler.control.events.remove(dpEvent);
            } else {
                matchedEvent.resource = resource.id.toString();
                matchedEvent.start = new DayPilot.Date(moment(event.startdatum).toDate(), true);
                matchedEvent.end = new DayPilot.Date(moment(event.slutdatum).toDate(), true);
                matchedEvent.arbetsorderRowVersion = event.arbetsorderRowVersion;
                matchedEvent.operationId = event.operationId;
                this.setDateError(matchedEvent);
                this.scheduler.control.events.update(matchedEvent);
            }
        }
    };

    private isArbetsorderInView(arbetsorderId: number): boolean {
        if (ObjectUtils.isNullOrUndefined(this.scheduler)) {
            return false;
        }
        const viewPort = this.scheduler.control.getViewPort();
        const viewPortEvents = (viewPort as any).events();
        const vieportEventMatch = viewPortEvents.find(e => e.data.arbetsorderId === arbetsorderId);

        if (vieportEventMatch) {
            return true;
        }
        return false;
    }

    private isOperationInDateView(operation: IOperation): boolean {
        const viewPort = this.scheduler.control.getViewPort();
        const viewPortStart = moment(viewPort.start.toDate());
        const viewPortEnd = moment(viewPort.end.toDate());
        const operationStart = moment(operation.startdatum);
        const operationEnd = moment(operation.slutdatum);
        const operationLengthDays = operationEnd.diff(operationStart, "days");
        const viewPortLengthDays = viewPortEnd.diff(viewPortStart, "days");

        // Om operationen är kortare än viewport så kollar vi att hela operationen får plats i vyn
        if (viewPortLengthDays >= operationLengthDays && operationStart >= viewPortStart && operationEnd <= viewPortEnd) {
            return true;
        }
        // Om operationen är bredare än viewport så kollar vi att operationens startdatum finns i viewport

        if (operationLengthDays > viewPortLengthDays && operationStart > viewPortStart && operationStart < viewPortEnd) {
            return true;
        }

        return false;
    }

    private async loadHelgdagar(fran: string, till: string) {
        const newDate = fran ? fran : this.defaultStartDate();

        this.calendarService.initHelgdagar(newDate, till);
    }

    private async loadMaskinlag(shortDate: string): Promise<void> {
        let maskinlag: IMaskinlagForArbetsorderResponse;
        if (this.arbetsorder) {
            maskinlag = await firstValueFrom(this.arbetsorderPlaneringSchedulerService
                .getMaskinlagForArbetsorder(this.arbetsorder.id, shortDate));
        } else {
            maskinlag = await firstValueFrom(this.arbetsorderPlaneringSchedulerService
                .getMinaMaskinlagAndOperationer(shortDate));
        }

        this.selectedMinaMaskinlag = maskinlag.maskinlag;

        if (shortDate) {
            this.lastFetchedMaskinlagDate = shortDate;
        }
    }

    private async moveOperationer(args) {
        const operationer = [];
        args.multimove.each(x => {
            operationer.push(x);
        });

        const commandManager = new CommandManager();
        const maskinlagsoperationer = groupBy(operationer, g => g.resource);

        values(maskinlagsoperationer).forEach(maskinlagOperationer => {
            maskinlagOperationer = orderBy(maskinlagOperationer, g => g.start) as any;

            const operationerToMove = maskinlagOperationer.map(((operation, index) => {
                const eventData = operation.event.data as IMaskinLagSchedulerEvent;
                const event = operation.event as DayPilot.EventData;
                const startDate = ((event.start as any)() as DayPilot.Date).toDate();
                let workdayGap = 0;
                if (index > 0) {
                    const prevEventEnd = moment(maskinlagOperationer[index - 1].event.end().toDate());
                    const momentStartDate = moment(startDate);
                    const totalDaysBetween = DateUtils.daysBetween(momentStartDate, prevEventEnd);

                    if (momentStartDate.isBefore(prevEventEnd)) {
                        workdayGap = totalDaysBetween - DateUtils.countDaysOfType(
                            momentStartDate,
                            prevEventEnd,
                            this.arbetsorderPlaneringSchedulerService.isWeekendOrHelgdag);
                    } else {
                        workdayGap = totalDaysBetween - DateUtils.countDaysOfType(
                            prevEventEnd,
                            momentStartDate,
                            this.arbetsorderPlaneringSchedulerService.isWeekendOrHelgdag);
                    }
                }

                return new Operation(
                    eventData.arbetsorderId,
                    eventData.operationId,
                    startDate,
                    ((event.end as any)() as DayPilot.Date).toDate(),
                    workdayGap,
                    this.arbetsorderPlaneringSchedulerService.isWeekendOrHelgdag);
            }));

            const shifter = new OperationShifter(operationerToMove, this.arbetsorderPlaneringSchedulerService.isWeekendOrHelgdag);
            const daysShifted = DateUtils.daysBetween(maskinlagOperationer[0].start.toDate(), maskinlagOperationer[0].event.start().toDate());
            const commands = shifter.shiftDays(daysShifted);
            commands.forEach(commandManager.updateCommand);
        });

        await this.invokeWithArgs(args, commandManager);
    }

    private readonly setTimeHeaders = (zoom: number) => {
        const dayResolutionChanged = (): boolean => {
            // eslint-disable-next-line sonarjs/prefer-single-boolean-return
            if ((!this.dayResolution && zoom >= this.dayResolutionThreshold) ||
                (this.dayResolution && zoom <= this.dayResolutionThreshold)) {
                return true;
            }

            return false;
        };

        if (dayResolutionChanged()) {
            this.dayResolution = !this.dayResolution;
            this.timeHeaders = this.getTimeHeadersForZoomLevel(zoom);
            this.schedulerConfig.timeHeaders = this.timeHeaders;
            this.scheduler.control.timeHeaders = this.timeHeaders;
        }
    };

    private readonly setZoom = (zoom: number) => {
        if (this.zoomDebounce) {
            this.zoomDebounce.cancel();
        }

        const start = this.scheduler.control.getViewPort().start;
        const viewPortStartDate = DateUtils
            .shortDateString(moment((start as any).value).startOf("d"));

        const configStartDate = DateUtils
            .shortDateString(moment((this.schedulerConfig.startDate as any).value).startOf("d"));

        this.scheduler.control.cellWidth = zoom;
        this.schedulerConfig.cellWidth = zoom;

        if (moment(viewPortStartDate).isBefore(configStartDate)) {
            this.scheduler.control.startDate = viewPortStartDate;
            this.schedulerConfig.startDate = viewPortStartDate;
            this.scheduler.control.update();
        }

        this.setTimeHeaders(zoom);
        this.scheduler.control.scrollTo(viewPortStartDate);

        this.zoomDebounce = debounce(() => {
            this.arbetsorderPlaneringSchedulerService.setZoom(zoom);
            this.zoomDebounce = undefined;
        }, 200);

        this.zoomDebounce();
    };

    private readonly onZoomChanged = (event: any) => {
        if (!event.ctrlKey) {
            return;
        }

        event.stopPropagation();
        event.preventDefault();

        let nextZoom: number;
        if (event.deltaY < 0 && this.scheduler.control.cellWidth < this.maxZoom) {
            nextZoom = Math.min(this.arbetsorderPlaneringSchedulerService
                .getZoomDelta(this.scheduler.control.cellWidth, false), this.maxZoom);
        } else if (event.deltaY > 0 && this.scheduler.control.cellWidth > this.minZoom) {
            nextZoom = Math.max(this.arbetsorderPlaneringSchedulerService
                .getZoomDelta(this.scheduler.control.cellWidth, true), this.minZoom);
        }

        if (nextZoom) {
            this.setZoom(nextZoom);
        }
    };

    private openPopover(arg: any, schemaPopupInfo: SchemaPopupInfoDTO): void {
        const popoverClickWrapper = this.getPopOverDiv(arg.div);
        const compRef = createComponent(ArbetsorderSchedulerPopoverNgxComponent, {
            environmentInjector: this.envInjector,
            hostElement: popoverClickWrapper
        });
        this.appRef.attachView(compRef.hostView);

        const eventData: IMaskinLagSchedulerEvent = arg.e.data;

        compRef.instance.setData(schemaPopupInfo, eventData, eventData.arbetsorderId);

        setTimeout(() => {
            this.closePopover();
            this.activePopover = compRef;
            compRef.instance.popover.open();
        }, 0);
    }

    private scrollToDate(date: Date): void {
        window.requestAnimationFrame(() => {
            this.scheduler.control.infinite.scrollTo(new DayPilot.Date(date, true));
        });
    }

    private readonly syncArbetsorderRowVersion = () => {
        if (this.arbetsorder && this.arbetsorder.id) {
            this.schedulerEvents
                .filter(p => p.arbetsorderId === this.arbetsorder.id)
                .forEach(p => {
                    p.arbetsorderRowVersion = this.arbetsorder.rowVersion;
                    p.cssClass = "";
                });
        }
    };

    private syncPreviousArbetsorder(arbetsorderId?: number) {
        if (arbetsorderId) {
            this.schedulerEvents
                .filter(p => p.arbetsorderId === arbetsorderId)
                .forEach(p => {
                    p.cssClass = ArbetsorderPlaneringSchedulerComponent.cssClassActiveEvent;
                });
        }
    }
}
